import { BuilderStoreContext } from "@builder.io/react";
// @ts-expect-error this module is not exported, we imported as a workaround for a bug in builder.io integration
import { tryEval } from "@builder.io/react/dist/lib/src/functions/try-eval";
import { logError } from "@faire/web/common/logging";
import { Typography } from "@faire/web/slate/Typography";
import { Props as TypographyProps } from "@faire/web/slate/Typography/Typography";
import parse from "html-react-parser";
import { sanitize, addHook } from "isomorphic-dompurify";
import React from "react";

import { BuilderAttributes } from "./BuilderTypes";

// This is a replicate of the evalExpression function in Builder's native Text component
// https://github.com/BuilderIO/builder/blob/main/packages/react/src/blocks/Text.tsx#L27
const evalExpression = (expression: string, state: any): string => {
  try {
    return String(expression).replace(/{{([^}]+)}}/g, (_, group) =>
      tryEval(group, state)
    );
  } catch (error) {
    logError(
      `Builder Typography failed to evaluate expression ${expression}: ${error}`
    );
    return expression;
  }
};

// Split each <p> element into a separate text group
const sanitizeAndGroupText = (textContent: string): string[] => {
  // From https://github.com/cure53/DOMPurify/blob/main/demos/hooks-target-blank-demo.html
  // This is a workaround to allow target="_blank" to be set on links
  addHook("afterSanitizeAttributes", (node) => {
    if ("target" in node) {
      node.setAttribute("target", "_blank");
      node.setAttribute("rel", "noopener noreferrer");
    }
  });

  const sanitizedTextContent = sanitize(textContent.trim());

  return sanitizedTextContent
    .split("<p>")
    .filter(Boolean)
    .flatMap((text) => {
      const parts = text.trim().split("</p>");

      return [parts[0] ?? "", parts[1] ?? ""].filter(Boolean);
    })
    .filter(Boolean);
};

const isBreak = (textGroup: string) => {
  return (
    textGroup === "<br />" || textGroup === "<br/>" || textGroup === "<br>"
  );
};

export const BuilderTypography = ({
  textContent,
  attributes,
  ...props
}: TypographyProps & {
  textContent: string;
  attributes: BuilderAttributes;
}) => {
  if (!textContent) {
    return null;
  }

  const textGroups = sanitizeAndGroupText(textContent);

  return (
    <BuilderStoreContext.Consumer>
      {(state) =>
        textGroups.map((textGroup, index) => {
          if (isBreak(textGroup)) {
            return <br key={`${attributes.key}-br-${index}`} />;
          }

          return (
            <Typography
              {...props}
              {...attributes}
              key={`${attributes.key}-${index}`}
              builder-id={attributes["builder-id"]}
            >
              {parse(evalExpression(textGroup, state.state))}
            </Typography>
          );
        })
      }
    </BuilderStoreContext.Consumer>
  );
};
